    import { Routes, UrlSegment } from '@angular/router';
    import { pageLayoutResolver } from './layout/resolvers/page-layout.resolver';
    import { homePageLayoutResolver } from './layout/resolvers/home-page-layout.resolver';
    import { blogLayoutResolver } from './layout/resolvers/blog-layout.resolver';
    import { blogPostResolver } from './layout/resolvers/blog-post.resolver';
    import { websiteLayoutResolver } from './layout/resolvers/website-layout.resolver';
    import { staticPageResolver } from './layout/resolvers/static-page-resolver';
    import { fallbackPageResolver } from './layout/resolvers/fallback-page.resolver';
    import { AuthGuard } from './guards/auth.guard';
    import { inject } from '@angular/core';
    import { GlobalService } from './common/services/global.service';
    import { filter, take } from 'rxjs/operators';

const isStateCode = (seg: UrlSegment) => /^[a-z]{2}$/.test(seg.path);

const NEWEST_HOMES_SLUG = 'newest-homes';
const BROWSE_PARAM_KEYS = ['state', 'county', 'zip', 'street'] as const;

function makeStateMatcher(
    expectedLength: number,
    extraCheck?: (url: UrlSegment[]) => boolean,
) {
    return (url: UrlSegment[]) => {
        if (url.length !== expectedLength) return null;
        if (!isStateCode(url[0])) return null;
        if (extraCheck && !extraCheck(url)) return null;
        const posParams: Record<string, UrlSegment> = {};
        BROWSE_PARAM_KEYS.slice(0, expectedLength).forEach((key, i) => { posParams[key] = url[i]; });
        return { consumed: url, posParams };
    };
}


    export const routes: Routes = [
        {
            path: '',
            resolve: {
                layout: websiteLayoutResolver,
            },
            children: [
                {
                    path: '',
                    loadComponent: () =>
                        import('./pages/page-builder/page-builder.component').then(
                            (mod) => mod.PageBuilderComponent,
                        ),
                    resolve: {
                        response: homePageLayoutResolver,
                    },
                },
                {
                    path: 'account',
                    loadComponent: () =>
                        import('./pages/account/account.component').then((mod) => mod.AccountComponent),
                    canActivate: [AuthGuard],
                },
                // Owner view for a claimed home — carries the price estimate range
                // alongside the property summary, reached from the "Your Properties" tab.
                {
                    path: 'my-property/:slug',
                    loadComponent: () =>
                        import('./pages/my-property/my-property.component').then((mod) => mod.MyPropertyComponent),
                    canActivate: [AuthGuard],
                },
                {
                    path: 'unsubscribe',
                    loadComponent: () =>
                        import('./pages/unsubscribe-page/unsubscribe-page.component').then(
                            (mod) => mod.UnsubscribePageComponent,
                        ),
                },
                {
                    // Opt out of the automatic home suggestions, linked from those emails. Signed
                    // in like /unsubscribe: the link carries person_id + signature.
                    path: 'unsubscribe/home-suggestions',
                    loadComponent: () =>
                        import(
                            './pages/unsubscribe-home-suggestions/unsubscribe-home-suggestions.component'
                        ).then((mod) => mod.UnsubscribeHomeSuggestionsComponent),
                },
                {
                    path: 'blog',
                    loadComponent: () =>
                        import('./pages/page-builder/page-builder.component').then(
                            (mod) => mod.PageBuilderComponent,
                        ),
                    resolve: {
                        response: blogLayoutResolver,
                    },
                },
                {
                    path: 'blog/author/:slug',
                    loadComponent: () =>
                        import('./components/blog-category/blog-category.component').then(
                            (mod) => mod.BlogCategoryComponent,
                        ),
                    resolve: {
                        response: blogPostResolver,
                    },
                },
                {
                    path: 'blog/category/:slug',
                    loadComponent: () =>
                        import('./components/blog-category/blog-category.component').then(
                            (mod) => mod.BlogCategoryComponent,
                        ),
                    resolve: {
                        response: blogPostResolver,
                    },
                },
                {
                    path: 'blog/month/:slug',
                    loadComponent: () =>
                        import('./components/blog-category/blog-category.component').then(
                            (mod) => mod.BlogCategoryComponent,
                        ),
                    resolve: {
                        response: blogPostResolver,
                    },
                },
                {
                    path: 'blog/:slug',
                    loadComponent: () =>
                        import('./pages/blog-post/blog-post.component').then(
                            (mod) => mod.BlogPostComponent,
                        ),
                    resolve: {
                        response: blogPostResolver,
                    },
                },
                {
                    path: 'terms-of-service',
                    resolve: {
                        response: fallbackPageResolver,
                    },
                    loadComponent: () =>
                        import(
                            './components/terms-of-service/terms-of-service.component'
                        ).then((mod) => mod.TermsOfServiceComponent),
                },
                {
                    path: 'privacy-policy',
                    resolve: {
                        response: fallbackPageResolver,
                    },
                    loadComponent: () =>
                        import(
                            './components/privacy-policy/privacy-policy.component'
                        ).then((mod) => mod.PrivacyPolicyComponent),
                },
                {
                    path: '',
                    resolve:{
                        data: staticPageResolver,
                    },
                    runGuardsAndResolvers: 'always',
                    children: [
                        {
                            path: 'mortgage-calculator',
                            loadComponent: () =>
                                import(
                                    './components/property-detail/advanced-mortgage-calculator/advanced-mortgage-calculator.component'
                                ).then((mod) => mod.AdvancedMortgageCalculatorComponent),
                        },
                        {
                            path: 'agents/:url_slug',
                            loadComponent: () =>
                                import('./components/agent-detail/agent-detail.component').then(
                                    (mod) => mod.AgentDetailComponent,
                                )
                        },
                        {
                            path: 'sitemap',
                            children: [
                                {
                                    path: '',
                                    loadComponent: () =>
                                        import('./pages/sitemap/sitemap.component').then(
                                            (mod) => mod.SiteMapComponent,
                                        ),
                                },
                                {
                                    path: ':city',
                                    loadComponent: () =>
                                        import('./pages/sitemap-community/sitemap-community.component').then(
                                            (mod) => mod.SiteMapCommunityComponent,
                                        )
                                },
                            ],
                        },
                        {
                            path: 'sitemap-blogs',
                            loadComponent: () =>
                                import('./pages/sitemap-blogs/sitemap-blogs.component').then(
                                    (mod) => mod.SiteMapBlogsComponent,
                                ),
                        },
                        {
                            path: 'sitemap-pages',
                            loadComponent: () =>
                                import('./pages/sitemap-pages/sitemap-pages.component').then(
                                    (mod) => mod.SiteMapPagesComponent,
                                ),
                        },
                        {
                            path: 'sitemap-listings',
                            loadComponent: () =>
                                import('./pages/sitemap-listings/sitemap-listings.component').then(
                                    (mod) => mod.SiteMapListingsComponent,
                                ),
                        },
                        {
                            path: 'sitemap-blogs.html',
                            loadComponent: () =>
                                import('./pages/sitemap-blogs/sitemap-blogs.component').then(
                                    (mod) => mod.SiteMapBlogsComponent,
                                ),
                        },
                        {
                            path: 'sitemap-pages.html',
                            loadComponent: () =>
                                import('./pages/sitemap-pages/sitemap-pages.component').then(
                                    (mod) => mod.SiteMapPagesComponent,
                                ),
                        },
                        {
                            path: 'sitemap-listings.html',
                            loadComponent: () =>
                                import('./pages/sitemap-listings/sitemap-listings.component').then(
                                    (mod) => mod.SiteMapListingsComponent,
                                ),
                        },
                        {
                            path: 'schools',
                            loadComponent: () =>
                                import('./pages/schools/schools.component').then((mod) => mod.SchoolsComponent),
                        },
                        {
                            path: 'zipcodes',
                            loadComponent: () =>
                                import('./pages/zipcodes/zipcodes.component').then(
                                    (mod) => mod.ZipcodesComponent,
                                ),
                        },
                        {
                            path: ':city-state/schools',
                            loadComponent: () =>
                                import('./pages/schools/schools.component').then((mod) => mod.SchoolsComponent),
                        },
                        {
                            path: ':city-state/zipcodes',
                            loadComponent: () =>
                                import('./pages/zipcodes/zipcodes.component').then(
                                    (mod) => mod.ZipcodesComponent,
                                ),
                        },

                        // Browse hierarchy — canMatch gates access via domain allowlist check.
                        // If not allowed, route is skipped and ** renders NotFoundPageComponent at the same URL.
                        // BrowseHomesWrapperComponent renders <router-outlet> or <app-not-found-page> based on the flag.
                        // Children receive URL segments after /browse/homes.
                        {
                            path: 'browse/homes',
                            canMatch: [() => inject(GlobalService).closedPropertiesEnabled.pipe(
                                filter(v => v !== null),
                                take(1),
                            )],
                            loadComponent: () =>
                                import('./pages/browse/browse-homes-wrapper/browse-homes-wrapper.component').then(
                                    (m) => m.BrowseHomesWrapperComponent,
                                ),
                            children: [
                                {
                                    path: '',
                                    loadComponent: () =>
                                        import('./pages/browse/browse-states/browse-states.component').then(
                                            (m) => m.BrowseStatesComponent,
                                        ),
                                },
                                {
                                    matcher: makeStateMatcher(1),
                                    loadComponent: () =>
                                        import('./pages/browse/browse-counties/browse-counties.component').then(
                                            (m) => m.BrowseCountiesComponent,
                                        ),
                                },
                                {
                                    matcher: makeStateMatcher(2),
                                    loadComponent: () =>
                                        import('./pages/browse/browse-zips/browse-zips.component').then(
                                            (m) => m.BrowseZipsComponent,
                                        ),
                                },
                                {
                                    matcher: makeStateMatcher(3, url => url[2].path === NEWEST_HOMES_SLUG),
                                    loadComponent: () =>
                                        import('./pages/browse/browse-newest-homes/browse-newest-homes.component').then(
                                            (m) => m.BrowseNewestHomesComponent,
                                        ),
                                },
                                {
                                    matcher: makeStateMatcher(3, url => url[2].path !== NEWEST_HOMES_SLUG),
                                    loadComponent: () =>
                                        import('./pages/browse/browse-streets/browse-streets.component').then(
                                            (m) => m.BrowseStreetsComponent,
                                        ),
                                },
                                {
                                    matcher: makeStateMatcher(4),
                                    loadComponent: () =>
                                        import('./pages/browse/browse-properties/browse-properties.component').then(
                                            (m) => m.BrowsePropertiesComponent,
                                        ),
                                },
                            ],
                        },

                ]
                },
                {
                    loadComponent: () =>
                        import('./pages/page-builder/page-builder.component').then(
                            (mod) => mod.PageBuilderComponent,
                        ),
                    resolve: {
                        response: pageLayoutResolver,
                    },
                    matcher: (url) => {
                        if (url.length === 0) return null;
                        const path = url[url.length - 1].path;
                        if (url[0].path !== 'home' && !path.includes('.html') && !path.includes('.php') && path.includes('.')) return null;
                        return { consumed: url };
                    },

                    // {
                    //     path: ':state/:city/schools',
                    //     component: SchoolsComponent,
                    // },
                    // {
                    //     path: ':state/:city/zipcodes',
                    //     component: ZipcodesComponent,
                    // },
                    // {
                    //     path: ':state/:city/:slug',
                    //     component: PageBuilderComponent,
                    //     resolve: {
                    //         response: pageLayoutResolver,
                    //     },
                    //     data: routeData,
                    // },
                    // {
                    //     path: '**',
                    //     component: PageBuilderComponent,
                    //     resolve: {
                    //         response: pageLayoutResolver,
                    //     },
                    // },
                },
                {
                    path: '**',
                    loadComponent: () =>
                        import('./pages/not-found/not-found-page.component').then(
                            (mod) => mod.NotFoundPageComponent,
                    ),
                },
            ],
        },
    ];
